import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';
import { LockIcon, MailIcon, AlertCircleIcon } from 'lucide-react';
const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const {
    login
  } = useAdmin();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as any)?.from || '/admin';
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const success = await login(email, password);
      if (success) {
        navigate(from, {
          replace: true
        });
      } else {
        setError('Invalid email or password.');
      }
    } catch (err) {
      setError('An error occurred during login.');
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden" data-id="element-1830">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-64 bg-blue-950 rounded-b-[100px] shadow-lg" data-id="element-1831"></div>

      <div className="max-w-md w-full space-y-8 relative z-10" data-id="element-1832">
        <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100" data-id="element-1833">
          <div className="text-center mb-8" data-id="element-1834">
            <div className="w-16 h-16 bg-yellow-500 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-md transform rotate-3" data-id="element-1835">
              <span className="text-2xl font-bold text-blue-950" data-id="element-1836">IGS</span>
            </div>
            <h2 className="text-3xl font-extrabold text-blue-950" data-id="element-1837">
              Admin Portal
            </h2>
            <p className="mt-2 text-sm text-gray-600" data-id="element-1838">
              Sign in to manage the school website
            </p>
          </div>

          {error && <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-md flex items-start" data-id="element-1839">
              <AlertCircleIcon className="h-5 w-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-1840" />
              <p className="text-sm text-red-700" data-id="element-1841">{error}</p>
            </div>}

          <form className="space-y-6" onSubmit={handleSubmit} data-id="element-1842">
            <div data-id="element-1843">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1844">
                Email Address
              </label>
              <div className="relative" data-id="element-1845">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1846">
                  <MailIcon className="h-5 w-5 text-gray-400" data-id="element-1847" />
                </div>
                <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-colors" placeholder="admin@igs.edu.ng" data-id="element-1848" />
              </div>
            </div>

            <div data-id="element-1849">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1850">
                Password
              </label>
              <div className="relative" data-id="element-1851">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1852">
                  <LockIcon className="h-5 w-5 text-gray-400" data-id="element-1853" />
                </div>
                <input type="password" required value={password} onChange={e => setPassword(e.target.value)} className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-colors" placeholder="••••••••" data-id="element-1854" />
              </div>
            </div>

            <div className="flex items-center justify-between" data-id="element-1855">
              <div className="flex items-center" data-id="element-1856">
                <input id="remember-me" name="remember-me" type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-1857" />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700" data-id="element-1858">
                  Remember me
                </label>
              </div>
              <div className="text-sm" data-id="element-1859">
                <a href="#" className="font-medium text-blue-600 hover:text-blue-500" data-id="element-1860">
                  Forgot password?
                </a>
              </div>
            </div>

            <button type="submit" disabled={isLoading} className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-blue-950 bg-yellow-500 hover:bg-yellow-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-all ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-1861">
              {isLoading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          <div className="mt-8 bg-blue-50 p-4 rounded-lg border border-blue-100" data-id="element-1862">
            <h4 className="text-xs font-bold text-blue-900 uppercase tracking-wider mb-2" data-id="element-1863">
              Demo Credentials
            </h4>
            <div className="text-sm text-blue-800 space-y-1 font-mono" data-id="element-1864">
              <p data-id="element-1865">
                Email: <span className="font-bold" data-id="element-1866">admin@igs.edu.ng</span>
              </p>
              <p data-id="element-1867">
                Password: <span className="font-bold" data-id="element-1868">admin2026</span>
              </p>
            </div>
          </div>
        </div>

        <p className="text-center text-sm text-gray-500" data-id="element-1869">
          &copy; {new Date().getFullYear()} Isonyin Grammar School. All rights
          reserved.
        </p>
      </div>
    </div>;
};
export default AdminLogin;